import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

import {
  AppBar,
  Toolbar,
  IconButton,
  Icon,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Divider,
  Drawer,
  Modal,
  Box,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import { useUser } from "context/UserContext";
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import { googleLogout } from "@react-oauth/google";
import { useDomain } from "context/DomainContext";
import { FAST_API_BASE_URL } from "constants";
import axios from "axios";
import { useSites } from "context/SitesContext";
import { LAMBDA_BASE_URL } from "constants";
import { set } from "date-fns";

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [domainUsersCount, setDomainUsersCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addDomainModalOpen, setAddDomainModalOpen] = useState(false);
  const [domainRegistrationLoading, setDomainRegistrationLoading] = useState(false);
  const [domainFormData, setDomainFormData] = useState({
    domainName: "",
    websitePlatform: "",
    companyName: "",
  });

  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [isDomainCheckLoading, setIsDomainCheckLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { googleUser, setGoogleUser } = useUser();
  const { sites, setSites } = useSites();
  const { setDomainId, setSource } = useDomain();

  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;

  const customUser = sessionStorage.getItem("custom") === "true";
  const customEmail = sessionStorage.getItem("customEmail");
  const domainId = sessionStorage.getItem("domainId");

  const isLoggedIn = googleUser || (customUser && customEmail);
  const userEmail = googleUser?.email || (customUser ? customEmail : "");
  const userName = sessionStorage.getItem("name") || (googleUser?.name || (customUser ? customEmail.split("@")[0] : ""));
  const [firstName, lastName] = userName.split(' ').length >= 2 
    ? [userName.split(' ')[0], userName.split(' ').slice(1).join(' ')]
    : [userName, ''];

  const passwordRules = {
    letter: /[A-Za-z]/.test(signupFormData.password),
    uppercase: /[A-Z]/.test(signupFormData.password),
    lowercase: /[a-z]/.test(signupFormData.password),
    number: /[0-9]/.test(signupFormData.password),
    specialChar: /[^A-Za-z0-9]/.test(signupFormData.password),
    minLength: signupFormData.password.length >= 8,
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchDomainUsersCount = async () => {
      if (isLoggedIn || !domainId) {
        setDomainUsersCount(null);
        setIsDomainCheckLoading(false);
        return;
      }

      setLoading(true);
      setIsDomainCheckLoading(true);
      try {
        const res = await axios.get(
          `${LAMBDA_BASE_URL}/domain-users-count`,
          {headers:{
            "DOMAIN-TOKEN": domainId,
          }}
        );
        
        if (res.data?.messages.count !== undefined) {
          setDomainUsersCount(res.data.messages.count);
          if (res.data.messages.count === 0) {
            setSignupDialogOpen(true);
          } else {
            setLoginDialogOpen(true);
          }
        } else {
          setDomainUsersCount(0);
          setSignupDialogOpen(true);
        }
      } catch (error) {
        console.error("Failed to fetch domain users count:", error);
        setDomainUsersCount(0);
        setSignupDialogOpen(true);
      } finally {
        setLoading(false);
        setIsDomainCheckLoading(false);
      }
    };

    fetchDomainUsersCount();
  }, [domainId, isLoggedIn]);

  useEffect(() => {
    if (signupDialogOpen) {
      setShowOtpField(false);
      setShowSignupForm(false);
      setShowPasswordField(false);
      setSignupEmail("");
      setOtp("");
      setLoginPassword("");
      setSignupFormData({
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
      });
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [signupDialogOpen]);

  useEffect(() => {
    if (loginDialogOpen) {
      setSignupEmail("");
      setLoginPassword("");
      setShowPassword(false);
    }
  }, [loginDialogOpen]);

  const websitePlatforms = [
    { value: "wordpress", label: "WordPress" },
    { value: "webflow", label: "Webflow" },
    { value: "wix", label: "Wix" },
    { value: "squarespace", label: "Squarespace" },
    { value: "custom", label: "Others" },
  ];

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const res = await axios.get(`${FAST_API_BASE_URL}/api/user/domains`, {
          headers: {
            EMAIL: googleUser?.email || (customUser ? customEmail : ""),
          },
        });

        if (res.data?.result && Array.isArray(res.data.result.domains)) {
          setSites(res.data.result.domains);
        }
      } catch (error) {
        console.error("Failed to fetch sites:", error);
      }
    };

    if (googleUser?.email || (customUser && customEmail)) {
      fetchSites();
    }
  }, [googleUser,customEmail, customUser]);

  useEffect(() => {
    const type = fixedNavbar ? "sticky" : "static";
    setNavbarType(type);

    const handleTransparentNavbar = () => {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    const storedDomain = sessionStorage.getItem("domainId");
    if (storedDomain) {
      setSelectedSite(storedDomain);
      setDomainId(storedDomain);
    }

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar, setDomainId]);

  useEffect(() => {
    if (sites.length === 0) return;

    const storedDomainId = sessionStorage.getItem("domainId");
    const storedDomainName = sessionStorage.getItem("domain_name");

    if (storedDomainId && storedDomainName) {
      const isValid = sites.some((site) => site.domain_token === storedDomainId);
      if (isValid) {
        setSelectedSite(storedDomainId);
        setDomainId(storedDomainId);
        return;
      }
    }

    const firstSite = sites[0];
    if (firstSite) {
      setSelectedSite(firstSite.domain_token);
      setSource(firstSite.source);
      setDomainId(firstSite.domain_token);
      sessionStorage.setItem("domainId", firstSite.domain_token);
      sessionStorage.setItem("domain_name", firstSite.domain_name);
      sessionStorage.setItem("role", firstSite.role);
      sessionStorage.setItem("source", firstSite.source);

      if (firstSite.role?.toLowerCase() === "read only" && route[0] !== "dashboard") {
        navigate("/dashboard");
      }
      if(firstSite.role==null){
        navigate("/customers");
      }
    }
  }, [sites, setDomainId, setSource, navigate, route]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    googleLogout();
    setGoogleUser(null);
    localStorage.removeItem("googleUser");
    sessionStorage.removeItem("googleUser");
    sessionStorage.clear();
    handleMenuClose();
    navigate("/login");
  };

  const getSelectedSiteName = () => {
    if (selectedSite) {
      return (
        sites.find((s) => s.domain_token === selectedSite)?.domain_name ||
        "Select Site"
      );
    }
    return sessionStorage.getItem("domain_name") || "Select Site";
  };

  const handleLogin = () => {
    navigate(`/login`);
  };

  const handleGoogleSignIn = async (credentialResponse) => {
    setGoogleSignInLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      const headers = {};
      if (domainId) {
        headers["DOMAIN-TOKEN"] = domainId;
      }

      const payload = {
        mail: decoded.email,
        name: decoded.name,
        profile_pic: decoded.picture,
        role: "",
        type: "google",
      };

      const response = await axios.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
        headers,
      });

      if (response.status === 200 && response.data) {
        sessionStorage.setItem("user_details", JSON.stringify(response.data));
        sessionStorage.setItem("name", response.data.result.name);
        setSelectedSite(domainId);
        setGoogleUser(decoded);
        setDomainId(domainId);
        setSource(response.data.result.source);
        sessionStorage.setItem("domainId", domainId);
        sessionStorage.setItem("domain_name", response.data.result.domain_name);
        sessionStorage.setItem("role", response.data.result.role);
        sessionStorage.setItem("source", response.data.result.source);

        if (response.data.result.is_super_admin === 1) {
          sessionStorage.setItem("is_super_admin", true);
        } else {
          sessionStorage.setItem("is_super_admin", false);
        }

        setSignupDialogOpen(false);
        setLoginDialogOpen(false);
        showSnackbar("Successfully signed in with Google!", "success");
        
        setTimeout(() => {
          const { role, domains, is_super_admin } = response.data.result || {};
          let finalRole = role || null;
          let domainId = null;
        
          if (is_super_admin === "true") {
            sessionStorage.setItem("is_super_admin", "true");
            navigate("/customers");
            return; 
          }
          
          if (domains && domains.length > 0) {
            finalRole = domains[0].role;
            domainId = domains[0].domain_id;
            sessionStorage.setItem("domainId", domainId);
          }
          
          if (finalRole) {
            sessionStorage.setItem("role", finalRole);
          }
          
         navigate("/dashboard");
        }, 1000);

      }
    } catch (error) {
      console.error("Google login error:", error);
      if (error.response && error.response.status === 401) {
        const decoded = jwtDecode(credentialResponse.credential);
        const nameParts = decoded.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        setSignupEmail(decoded.email);
        setSignupFormData({
          firstName: firstName,
          lastName: lastName,
          password: "",
          confirmPassword: "",
        });
        setShowSignupForm(true);
        setLoginDialogOpen(false);
        setSignupDialogOpen(true);
      } else {
        showSnackbar("Google sign-in failed. Please try again.", "error");
      }
    } finally {
      setGoogleSignInLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupEmail || !signupEmail.includes('@')) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    setSignupLoading(true);

    try {
      const response = await axios.post(`${FAST_API_BASE_URL}/api/signup`, {
        email: signupEmail,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        setShowOtpField(true);
        setShowPasswordField(false);
        showSnackbar("Verification code sent to your email!", "success");
      }
    } catch (error) {
      console.error("Signup failed:", error);
      
      if (error.response && error.response.status === 409) {
        setShowPasswordField(true);
        setShowOtpField(false);
      } else {
        showSnackbar(error.response?.data?.messages?.[0] || "Signup failed. Please try again.", "error");
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!loginPassword) {
      showSnackbar("Please enter your password", "error");
      return;
    }

    setLoginLoading(true);

    try {
      const headers = {};
      if (domainId) {
        headers["DOMAIN-TOKEN"] = domainId;
      }

      const payload = {
        mail: signupEmail,
        password: loginPassword,
        type: "custom",
        role:"admin"
      };

      const response = await axios.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data) {
        sessionStorage.setItem("user_details", JSON.stringify(response.data));
        sessionStorage.setItem("name", response.data.result.name);
        sessionStorage.setItem("custom", true);
        sessionStorage.setItem("customEmail", signupEmail);
        setSelectedSite(domainId);
        setDomainId(domainId);
        setSource(response.data.result.source);
        sessionStorage.setItem("domainId", domainId);
        sessionStorage.setItem("domain_name", response.data.result.domain_name);
        sessionStorage.setItem("role", response.data.result.role);
        sessionStorage.setItem("source", response.data.result.source);
        
        if (response.data.result.is_super_admin === 1) {
          sessionStorage.setItem("is_super_admin", true);
        } else {
          sessionStorage.setItem("is_super_admin", false);
        }

        setSignupDialogOpen(false);
        setLoginDialogOpen(false);
        showSnackbar("Successfully logged in!", "success");
        
        setTimeout(() => {
          const { role, domains, is_super_admin } = response.data.result || {};
          let finalRole = role || null;
          let domainId = null;
        
          if (is_super_admin === 1 || is_super_admin === "true") {
            sessionStorage.setItem("is_super_admin", "true");
            navigate("/customers");
            return; 
          }
          
          if (domains && domains.length > 0) {
            finalRole = domains[0].role;
            domainId = domains[0].domain_id;
            sessionStorage.setItem("domainId", domainId);
            sessionStorage.setItem("role", finalRole);
          }
          
          if (finalRole === "read only") {
            navigate("/dashboard");
          } else if (finalRole == null) {
            navigate("/customers");
          } else {
            navigate("/settings");
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Password login error:", error);
      showSnackbar(error.response?.data?.messages?.[0] || "Invalid credentials. Please try again.", "error");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLoginFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupEmail || !loginPassword) {
      showSnackbar("Please enter email and password", "error");
      return;
    }

    setLoginLoading(true);

    try {
      const headers = {};
      if (domainId) {
        headers["DOMAIN-TOKEN"] = domainId;
      }

      const payload = {
        mail: signupEmail,
        password: loginPassword,
        type: "custom",
        role: "admin"
      };

      const payload2 = {
        mail: signupEmail,
        password: loginPassword,
        type: "custom",
        role: "",
      };

      let response;
      if (Object.keys(headers).length > 0) {
        response = await axios.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        });
      } else {
        response = await axios.post(`${FAST_API_BASE_URL}/api/user/re-login`, payload2);
      }

      if (response.status === 200 && response.data) {
        sessionStorage.setItem("user_details", JSON.stringify(response.data));
        sessionStorage.setItem("name", response.data.result.name);
        sessionStorage.setItem("custom", true);
        sessionStorage.setItem("customEmail", signupEmail);
        setSelectedSite(domainId);
        setDomainId(domainId);
        setSource(response.data.result.source);
        sessionStorage.setItem("domainId", domainId);
        sessionStorage.setItem("domain_name", response.data.result.domain_name);
        sessionStorage.setItem("role", response.data.result.role);
        sessionStorage.setItem("source", response.data.result.source);
        
        if (response.data.result.is_super_admin === 1) {
          sessionStorage.setItem("is_super_admin", true);
        } else {
          sessionStorage.setItem("is_super_admin", false);
        }

        setLoginDialogOpen(false);
        showSnackbar("Successfully logged in!", "success");
        
        setTimeout(() => {
          const { role, domains, is_super_admin } = response.data.result || {};
          let finalRole = role || null;
          let domainId = null;
        
          if (is_super_admin === 1 || is_super_admin === "true") {
            sessionStorage.setItem("is_super_admin", "true");
            navigate("/customers");
            return; 
          }
          
          if (domains && domains.length > 0) {
            finalRole = domains[0].role;
            domainId = domains[0].domain_id;
            sessionStorage.setItem("domainId", domainId);
            sessionStorage.setItem("role", finalRole);
          }
          
          if (finalRole === "read only") {
            navigate("/dashboard");
          } else if (finalRole == null) {
            navigate("/customers");
          } else {
            navigate("/settings");
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Login error:", error);
      showSnackbar(error.response?.data?.messages?.[0] || "Invalid credentials. Please try again.", "error");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length < 4) {
      showSnackbar("Please enter a valid OTP", "error");
      return;
    }

    setOtpLoading(true);

    try {
      const response = await axios.post(`${FAST_API_BASE_URL}/api/verify-otp`, {
        email: signupEmail,
        otp: otp,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        showSnackbar("Email verified successfully!", "success");
        setShowSignupForm(true);
        setShowOtpField(false);
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      showSnackbar("Invalid OTP. Please try again.", "error");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSignupFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.password || !signupFormData.confirmPassword) {
      showSnackbar("Please fill in all password fields", "error");
      return;
    }

    if (signupFormData.password !== signupFormData.confirmPassword) {
      showSnackbar("Passwords do not match!", "error");
      return;
    }

    if (!allRulesPassed) {
      showSnackbar("Password does not meet the required criteria", "error");
      return;
    }

    setSignupLoading(true);

    try {
      const payload = {
        password: signupFormData.password,
        email: signupEmail,
        user_name: `${signupFormData.firstName} ${signupFormData.lastName}`.trim(),
        role: "admin",
      };

      const headers = {};
      if (domainId) {
        headers["DOMAIN-TOKEN"] = domainId;
      }

      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/save-password`,
        payload,
        {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showSnackbar("Account created successfully! Redirecting...", "success");
        setSignupDialogOpen(false);
        setSignupEmail("");
        setSignupFormData({
          firstName: "",
          lastName: "",
          password: "",
          confirmPassword: "",
        });
        setShowSignupForm(false);

        sessionStorage.setItem("name", `${signupFormData.firstName} ${signupFormData.lastName}`.trim());
        sessionStorage.setItem("custom", true);
        sessionStorage.setItem("customEmail", signupEmail);
        sessionStorage.setItem("role", "admin");
        setSelectedSite(domainId)
        sessionStorage.setItem("domainId", domainId);
        
        setTimeout(() => {
          navigate("/dashboard");
        }, 1000);
      }
    } catch (err) {
      console.error("Final signup failed:", err);
      showSnackbar("Failed to create account! Please try again.", "error");
    } finally {
      setSignupLoading(false);
    }
  };

  const extractDomainName = (input) => {
    if (!input) return '';
    
    let domain = input.trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '');
    domain = domain.split('/')[0];
    domain = domain.split(':')[0];
    
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    
    return domain;
  };

  const handleDomainFormChange = (e) => {
    const { name, value } = e.target;
    setDomainFormData({
      ...domainFormData,
      [name]: value,
    });
  };

  const handleDomainSelectChange = (name, value) => {
    setDomainFormData({
      ...domainFormData,
      [name]: value,
    });
  };

  const handleAddDomainSubmit = async (e) => {
    e.preventDefault();

    if (!domainFormData.domainName || !domainFormData.websitePlatform || !domainFormData.companyName) {
      showSnackbar("Please fill in all fields", "error");
      return;
    }

    const cleanDomain = extractDomainName(domainFormData.domainName);

    if (cleanDomain === "abc.com") {
      showSnackbar("Please contact your admin to add user!", "warning");
      return;
    }

    setDomainRegistrationLoading(true);

    try {
      const payload1 = {
        domain_url: cleanDomain,
        source: domainFormData.websitePlatform.value,
        email: userEmail,
        first_name: firstName,
        last_name: lastName,
      };
      
      const payload = {
        domain: cleanDomain,
        source: domainFormData.websitePlatform.value,
        company_name: domainFormData.companyName.trim(),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        date_formate: "MM-DD-YYYY",
        email: userEmail,
        user_name: `${firstName} ${lastName}`,
        is_google_user: !!googleUser,
        is_user_logged_in:true
      };
      
      const response1 = await axios.post(`${LAMBDA_BASE_URL}/save-registration`, payload1, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });
      
      if (domainFormData.websitePlatform.value === "custom" || domainFormData.websitePlatform.value === "wix" || domainFormData.websitePlatform.value === "squarespace") {
        const response = await axios.post(`${FAST_API_BASE_URL}/api/register`, payload, {
          headers: {
            "X-API-KEY": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json",
          },
        });

        if(response.status === 200){
          sessionStorage.removeItem("settingsTabIndex")
          navigate("/settings")
          
        }
      }
      
      if (response1.status === 200) {
        showSnackbar("Domain added successfully!", "success");
        setAddDomainModalOpen(false);
        setDomainFormData({
          domainName: "",
          websitePlatform: "",
          companyName: "",
        });
        
        if (googleUser?.email || (customUser && customEmail)) {
          const res = await axios.get(`${FAST_API_BASE_URL}/api/user/domains`, {
            headers: {
              EMAIL: userEmail,
            },
          });
          if (res.data?.result && Array.isArray(res.data.result.domains)) {
            setSites(res.data.result.domains);
            setSelectedSite(res.data.result.domains[0].domain_token);
            setDomainId(res.data.result.domains[0].domain_token);
            setSource(res.data.result.domains[0].source);
            sessionStorage.setItem("domainId",res.data.result.domains[0].domain_token );
            sessionStorage.setItem("domain_name",res.data.result.domains[0].domain_name );
            sessionStorage.setItem("role",res.data.result.domains[0].role );
            sessionStorage.setItem("source",res.data.result.domains[0].source );
            setDrawerOpen(true)
          }        
        }       

        if (domainFormData.websitePlatform.value === "wordpress") {
          window.open(`https://wordpress.org/plugins/ansera-search/`, '_blank');
        } else if (domainFormData.websitePlatform.value === "webflow") {
          window.open("https://webflow.com/apps/detail/ansera-search", '_blank');
        }
      }
    } catch (err) {
      console.error("Domain registration failed:", err);
      showSnackbar("Domain registration failed. Please try again.", "error");
    } finally {
      setDomainRegistrationLoading(false);
    }
  };

  const openAddDomainModal = () => {
    setDrawerOpen(false);
    setAddDomainModalOpen(true);
  };

  const renderRule = (label, passed) => ( 
    <Box display="flex" alignItems="center" mb={1}> 
      {passed ? ( 
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "16px" }} />
      ) : ( 
        <CancelIcon color="error" sx={{ mr: 1, fontSize: "16px" }} /> 
      )} 
      <Typography variant="body2" color={passed ? "#219297" : "text.secondary"} sx={{ fontSize: "13px" }}> 
        {label} 
      </Typography> 
    </Box>
  );

  const renderAuthButtons = () => {
    if (isLoggedIn) return null;

    if (isDomainCheckLoading) {
      return (
        <Button
          variant="outlined"
          disabled
          sx={{
            color: "#219297",
            borderColor: "#219297",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            textTransform: "none",
            backgroundColor: "transparent",
            opacity: 0.7,
          }}
        >
          Loading...
        </Button>
      );
    }
    if (!domainId || domainUsersCount === null) {
      return (
        <Button
          variant="outlined"
          onClick={handleLogin}
          sx={{
            color: "#219297",
            borderColor: "#219297",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            textTransform: "none",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "#219297",
              color: "#219297",
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          Login
        </Button>
      );
    }

    if (domainUsersCount === 0) {
      return null;
    }

    if (domainUsersCount > 0) {
      return (
        <Button
          variant="outlined"
          onClick={() => setLoginDialogOpen(true)}
          sx={{
            color: "#219297",
            borderColor: "#219297",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            textTransform: "none",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "#219297",
              color: "#219297",
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          Login
        </Button>
      );
    }

    return (
      <Button
        variant="outlined"
        onClick={handleLogin}
        sx={{
          color: "#219297",
          borderColor: "#219297",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.875rem",
          px: 3,
          py: 1,
          textTransform: "none",
          backgroundColor: "transparent",
          "&:hover": {
            borderColor: "#219297",
            color: "#219297",
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
        }}
      >
        Login
      </Button>
    );
  };

  const renderSignupForm = () => (
    <form onSubmit={handleSignupFormSubmit}>
      <Box>
        <Box 
          sx={{ 
            mb: 3,
            p: isMobile ? 1.5 : 2,
            backgroundColor: "#f0fdfa",
            borderRadius: "10px",
            border: "1px solid #ccfbf1",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "13px" : "14px", mb: 1 }}>
            Completing signup for
          </Typography>
          <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", fontSize: isMobile ? "14px" : "16px" }}>
            {signupEmail}
          </Typography>
        </Box>
        
        <Grid container spacing={isMobile ? 1 : 2} mb={3}>
          <Grid item xs={12} sm={6}>
            <Box mb={2}>
              <label 
                htmlFor="firstName" 
                style={{ 
                  fontSize: isMobile ? "13px" : "14px", 
                  fontWeight: "600", 
                  marginBottom: "8px", 
                  display: "block",
                  color: "#374151"
                }}
              >
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={signupFormData.firstName}
                onChange={(e) => setSignupFormData(prev => ({...prev, firstName: e.target.value}))}
                required
                placeholder="First Name"
                style={{
                  width: "100%",
                  padding: isMobile ? "10px 12px" : "12px 14px",
                  fontSize: isMobile ? "13px" : "14px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  boxSizing: "border-box"
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box mb={2}>
              <label 
                htmlFor="lastName" 
                style={{ 
                  fontSize: isMobile ? "13px" : "14px", 
                  fontWeight: "600", 
                  marginBottom: "8px", 
                  display: "block",
                  color: "#374151"
                }}
              >
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={signupFormData.lastName}
                onChange={(e) => setSignupFormData(prev => ({...prev, lastName: e.target.value}))}
                required
                placeholder="Last Name"
                style={{
                  width: "100%",
                  padding: isMobile ? "10px 12px" : "12px 14px",
                  fontSize: isMobile ? "13px" : "14px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  boxSizing: "border-box"
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box mb={2} sx={{ position: "relative" }}>
          <label 
            htmlFor="password" 
            style={{ 
              fontSize: isMobile ? "13px" : "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: "#374151"
            }}
          >
            Password
          </label>
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            value={signupFormData.password}
            onChange={(e) => setSignupFormData(prev => ({...prev, password: e.target.value}))}
            required
            placeholder="Enter password"
            style={{
              width: "100%",
              padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
              fontSize: isMobile ? "13px" : "14px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxSizing: "border-box"
            }}
          />
          <IconButton
            onClick={() => setShowPassword(!showPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: isMobile ? "30px" : "34px",
              padding: "6px",
            }}
            size={isMobile ? "small" : "medium"}
          >
            {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} /> : <Visibility fontSize={isMobile ? "small" : "medium"} />}
          </IconButton>
        </Box>

        {signupFormData.password && (
          <Box mb={2} sx={{ mt: 1 }}>
            {renderRule("At least 8 characters", passwordRules.minLength)}
            {renderRule("Includes a number", passwordRules.number)}
            {renderRule("Includes a special character", passwordRules.specialChar)}
            {renderRule("Includes Uppercase [A-Z]", passwordRules.uppercase)}
            {renderRule("Includes Lowercase [a-z]", passwordRules.lowercase)}
          </Box>
        )}

        <Box mb={3} sx={{ position: "relative" }}>
          <label 
            htmlFor="confirmPassword" 
            style={{ 
              fontSize: isMobile ? "13px" : "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: "#374151"
            }}
          >
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={signupFormData.confirmPassword}
            onChange={(e) => setSignupFormData(prev => ({...prev, confirmPassword: e.target.value}))}
            required
            placeholder="Confirm password"
            style={{
              width: "100%",
              padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
              fontSize: isMobile ? "13px" : "14px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxSizing: "border-box"
            }}
          />
          <IconButton
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: isMobile ? "30px" : "34px",
              padding: "6px",
            }}
            size={isMobile ? "small" : "medium"}
          >
            {showConfirmPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} /> : <Visibility fontSize={isMobile ? "small" : "medium"} />}
          </IconButton>
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={signupLoading || !allRulesPassed}
          sx={{
            py: isMobile ? 1.25 : 1.75,
            backgroundColor: (!allRulesPassed) ? "#ccc" : "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: isMobile ? "14px" : "15px",
            textTransform: "none",
            boxShadow: allRulesPassed ? "0 4px 12px rgba(33, 146, 151, 0.3)" : "none",
            "&:hover": {
              backgroundColor: allRulesPassed ? "#1a7a7d" : "#ccc",
              boxShadow: allRulesPassed ? "0 6px 16px rgba(33, 146, 151, 0.4)" : "none",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              boxShadow: "none",
            },
          }}
        >
          {signupLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              {isMobile ? "Creating..." : "Creating Account..."}
            </Box>
          ) : (
            "Complete Signup"
          )}
        </Button>
      </Box>
    </form>
  );

  const renderLoginForm = () => (
    <form onSubmit={handleLoginFormSubmit}>
      <Box>
        <Box mb={3}>
          <label 
            htmlFor="login-email" 
            style={{ 
              fontSize: isMobile ? "13px" : "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: "#374151"
            }}
          >
            Email Address
          </label>
          <input
            id="login-email"
            type="email"
            value={signupEmail}
            onChange={(e) => setSignupEmail(e.target.value)}
            required
            placeholder="you@example.com"
            style={{
              width: "100%",
              padding: isMobile ? "12px 14px" : "14px 16px",
              fontSize: isMobile ? "14px" : "15px",
              border: "2px solid #e5e7eb",
              borderRadius: "10px",
              boxSizing: "border-box",
              outline: "none",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              backgroundColor: "#fafafa",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#219297";
              e.target.style.backgroundColor = "#ffffff";
              e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e5e7eb";
              e.target.style.backgroundColor = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
        </Box>

        <Box mb={2} sx={{ position: "relative" }}>
          <label 
            htmlFor="login-password" 
            style={{ 
              fontSize: isMobile ? "13px" : "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: "#374151"
            }}
          >
            Password
          </label>
          <input
            id="login-password"
            type={showPassword ? "text" : "password"}
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
            placeholder="Enter your password"
            style={{
              width: "100%",
              padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
              fontSize: isMobile ? "13px" : "14px",
              border: "2px solid #e5e7eb",
              borderRadius: "10px",
              boxSizing: "border-box",
              outline: "none",
              transition: "all 0.2s ease",
              backgroundColor: "#fafafa",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#219297";
              e.target.style.backgroundColor = "#ffffff";
              e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e5e7eb";
              e.target.style.backgroundColor = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
          <IconButton
            onClick={() => setShowPassword(!showPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: isMobile ? "30px" : "34px",
              padding: "6px",
            }}
            size={isMobile ? "small" : "medium"}
          >
            {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} /> : <Visibility fontSize={isMobile ? "small" : "medium"} />}
          </IconButton>
        </Box>

        <Box width="100%" display="flex" justifyContent="flex-end" sx={{ mb: 3 }}>
          {/* <Button
            variant="text"
            onClick={() => {
              // Handle forgot password
              showSnackbar("Forgot password functionality to be implemented", "info");
            }}
            sx={{
              color: "#219297",
              textTransform: "none",
              fontWeight: 500,
              fontSize: isMobile ? "12px" : "14px",
              "&:hover": {
                backgroundColor: "#f0fdfa",
              },
            }}
          >
            Forgot password?
          </Button> */}
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loginLoading}
          sx={{
            py: isMobile ? 1.25 : 1.75,
            backgroundColor: "#219297",
            color: "#ffffff",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: isMobile ? "14px" : "15px",
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
            "&:hover": {
              backgroundColor: "#1a7a7d",
              boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              boxShadow: "none",
            },
            transition: "all 0.2s ease",
          }}
        >
          {loginLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
              {isMobile ? "Logging in..." : "Logging in..."}
            </Box>
          ) : (
            "Sign In"
          )}
        </Button>

        <Box sx={{ my: isMobile ? 2 : 3, display: "flex", alignItems: "center" }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" sx={{ px: 2, color: "text.secondary", fontSize: isMobile ? "12px" : "14px" }}>
            OR
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <GoogleLogin
            theme="filled_blue"
            text="continue_with"
            onSuccess={handleGoogleSignIn}
            onError={() => console.log("Google login failed")}
            size={isMobile ? "medium" : "large"}
          />
        </Box>

        {googleSignInLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={isMobile ? 20 : 24} />
            <Typography variant="body2" sx={{ ml: 1, fontSize: isMobile ? "12px" : "14px" }}>
              Signing in with Google...
            </Typography>
          </Box>
        )}

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "12px" : "14px" }}>
            Don't have an account?{" "}
            <Button
              variant="text"
              onClick={() => {
                setLoginDialogOpen(false);
                setSignupDialogOpen(true);
              }}
              sx={{
                color: "#219297",
                textTransform: "none",
                fontWeight: 600,
                fontSize: isMobile ? "12px" : "14px",
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Sign up
            </Button>
          </Typography>
        </Box>
      </Box>
    </form>
  );

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#219297"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ed6c02"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <AppBar
        position={navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <SoftBox
            color="inherit"
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { isMini })}
          >
            <Breadcrumbs
              icon="home"
              title={route[route.length - 1]}
              route={route}
              light={light}
            />
            <Icon
              fontSize="medium"
              sx={navbarDesktopMenu}
              onClick={handleMiniSidenav}
            >
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </SoftBox>

          {!isMini && (
            <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
              <SoftBox
                color={light ? "white" : "inherit"}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <IconButton
                  size="small"
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon className={light ? "text-white" : "text-dark"}>
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>

                {isLoggedIn ? (
                  <>
                    <SoftBox>
                      <Button
                        variant="outlined"
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                          color: "#000",
                          borderColor: "#000",
                          borderRadius: "8px",
                          fontWeight: 500,
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          px: isMobile ? 1.5 : 2,
                          py: isMobile ? 0.75 : 1,
                          textTransform: "none",
                          backgroundColor: "#fff",
                          "&:hover": {
                            borderColor: "teal",
                            color: "teal",
                            backgroundColor: "#f0fdfa",
                          },
                          "&.Mui-focused, &:focus, &:active": {
                            borderColor: "teal",
                            color: "teal",
                            backgroundColor: "#e0f7f7",
                          },
                        }}
                      >
                        {getSelectedSiteName()}
                      </Button>
                    </SoftBox>

                   <Drawer
                      anchor="right"
                      open={drawerOpen}
                      onClose={() => setDrawerOpen(false)}
                      PaperProps={{
                        sx: {
                          width: isMobile ? 280 : 300,
                          maxWidth: "85vw",
                        },
                      }}
                    >
                      <SoftBox p={isMobile ? 2 : 3} display="flex" flexDirection="column" height="100%">
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <SoftTypography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                            Select a Site
                          </SoftTypography>
                          <IconButton onClick={() => setDrawerOpen(false)} size={isMobile ? "small" : "medium"}>
                            <Icon>close</Icon>
                          </IconButton>
                        </SoftBox>
                        <MenuItem
                          onClick={openAddDomainModal}
                          sx={{
                            mb: 2,
                            py: isMobile ? 1 : 1.5,
                            px: isMobile ? 1.5 : 2,
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #219297",
                            color: "#219297",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": {
                              backgroundColor: "#1a7a7d",
                              color: "#ffffff",
                            },
                          }}
                        >
                          <Icon sx={{ color: "#219287" }}>add</Icon>
                          Add Domain
                        </MenuItem>
                        
                        <SoftBox
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": {
                              width: "2px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#cfcfcfff",
                              borderRadius: "3px",
                            },
                          }}
                        >
                          {sites.map((site) => {
                            const isSelected = selectedSite === site.domain_token;
                            if (site.is_deleted === 1 && sessionStorage.getItem("is_super_admin") == "false") return null;
                            return (
                              <MenuItem
                                key={site.domain_token}
                                onClick={() => {
                                  setSelectedSite(site.domain_token);
                                  setDomainId(site.domain_token);
                                  setSource(site.source);
                                  sessionStorage.setItem("domainId", site.domain_token);
                                  sessionStorage.setItem("domain_name", site.domain_name);
                                  sessionStorage.setItem("role", site.role);
                                  sessionStorage.setItem("source", site.source);
                                  setDrawerOpen(false);
                                  if (site.role?.toLowerCase() === "read only" && route[0] !== "dashboard") {
                                    navigate("/dashboard");
                                  }
                                  if (site.role == null) {
                                    navigate("/dashboard");
                                  }
                                }}
                                sx={{
                                  mb: 1,
                                  py: isMobile ? 1 : 1.5,
                                  px: isMobile ? 1.5 : 2,
                                  borderRadius: "8px",
                                  backgroundColor: isSelected ? "#219297" : "transparent",
                                  fontWeight: isSelected ? "bold" : "normal",
                                  color: isSelected ? "#ffffff" : "#000000",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  "&:hover": {
                                    backgroundColor: "#e0f7fa",
                                  },
                                }}
                              >
                                <Icon sx={{ color: "teal" }}>public</Icon>
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: isMobile ? "160px" : "180px",
                                    display: "inline-block",
                                    fontSize: isMobile ? "14px" : "16px",
                                  }}
                                >
                                  {site.domain_name}
                                </span>
                              </MenuItem>
                            );
                          })}
                        </SoftBox>
                      </SoftBox>
                    </Drawer>

                    <IconButton
                      onClick={handleMenuOpen}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <Avatar
                        src={
                          googleUser?.picture ||
                          (customUser ? customEmail.charAt(0).toUpperCase() : "")
                        }
                        alt={
                          googleUser?.name ||
                          (customUser ? customEmail.charAt(0).toUpperCase() : "")
                        }
                        sx={{ 
                          width: isMobile ? 32 : 36, 
                          height: isMobile ? 32 : 36, 
                          bgcolor: customUser ? "#219297" : "transparent",
                          color: customUser ? "white" : "inherit",
                        }}
                      />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        elevation: 4,
                        sx: {
                          mt: 1.5,
                          borderRadius: 2,
                          overflow: "visible",
                          filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
                          minWidth: isMobile ? 240 : 260,
                          p: 1,
                          "& .MuiAvatar-root": {
                            width: isMobile ? 40 : 48,
                            height: isMobile ? 40 : 48,
                            ml: 1,
                            mr: 1.5,
                          },
                          "&:before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: 0,
                            right: 20,
                            width: 10,
                            height: 10,
                            bgcolor: "background.paper",
                            transform: "translateY(-50%) rotate(45deg)",
                            zIndex: 0,
                          },
                        },
                      }}
                      transformOrigin={{
                        horizontal: "right",
                        vertical: "top",
                      }}
                      anchorOrigin={{
                        horizontal: "right",
                        vertical: "bottom",
                      }}
                    >
                      <MenuItem disableRipple sx={{ py: isMobile ? 1 : 1.5 }}>
                        <Avatar
                          src={googleUser?.picture || undefined}
                          sx={{
                            bgcolor: customUser ? "#219297" : "transparent",
                            color: customUser ? "white" : "inherit",
                          }}
                        >
                          {customUser ? customEmail.charAt(0).toUpperCase() : ""}
                        </Avatar>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>
                            {userName}
                          </div>
                          <div style={{ fontSize: isMobile ? "0.75rem" : "0.85rem", color: "#555" }}>
                            {googleUser?.email || (customUser ? customEmail : "")}
                          </div>
                        </div>
                      </MenuItem>
                      <Divider sx={{ my: 1 }} />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{ fontWeight: 500, alignContent: "center", fontSize: isMobile ? "14px" : "16px" }}
                      >
                        <Icon sx={{ fontSize: isMobile ? 16 : 18, mr: 1 }}>logout</Icon> Sign out
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  renderAuthButtons()
                )}
              </SoftBox>
            </SoftBox>
          )}
        </Toolbar>
      </AppBar>

      <Dialog
        open={signupDialogOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: isMobile ? "16px" : "32px",
            maxHeight: isMobile ? "calc(100% - 32px)" : "calc(100% - 64px)",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 2 : 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            width: "100%",
            maxWidth: isMobile ? "calc(100vw - 32px)" : "500px",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          pt: isMobile ? 3 : 4, 
          px: isMobile ? 3 : 4,
          flexShrink: 0,
        }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              sx={{
                width: isMobile ? 48 : 64,
                height: isMobile ? 48 : 64,
                borderRadius: "50%",
                backgroundColor: "#e0f7f7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: isMobile ? 24 : 32, color: "#219297" }}>
                {showPasswordField ? "lock" : showOtpField ? "lock" : showSignupForm ? "person" : "mail"}
              </Icon>
            </Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ mb: 1, color: "#1a1a1a" }}>
              {showSignupForm 
                ? "Complete Your Signup" 
                : showPasswordField
                ? "Welcome Back"
                : showOtpField 
                ? "Verify Your Email" 
                : "Welcome!"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              maxWidth: 380,
              fontSize: isMobile ? "13px" : "14px",
            }}>
              {showSignupForm 
                ? "Set up your password to complete registration"
                : showPasswordField
                ? "Enter your password to continue"
                : showOtpField 
                ? "Enter the verification code we sent to your email"
                : "Sign up to access your dashboard"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          px: isMobile ? 3 : 4, 
          pb: isMobile ? 3 : 4, 
          pt: isMobile ? 1 : 3,
          overflow: "auto",
          flex: 1,
        }}>
          {showSignupForm ? (
            renderSignupForm()
          ) : showPasswordField ? (
            <form onSubmit={handlePasswordLogin}>
              <Box>
                <Box 
                  sx={{ 
                    mb: 3,
                    p: isMobile ? 1.5 : 2,
                    backgroundColor: "#f0fdfa",
                    borderRadius: "10px",
                    border: "1px solid #ccfbf1",
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "13px" : "14px", mb: 1 }}>
                    Welcome back! Logging in as
                  </Typography>
                  <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", fontSize: isMobile ? "14px" : "16px" }}>
                    {signupEmail}
                  </Typography>
                </Box>
                
                <Box mb={3} sx={{ position: "relative" }}>
                  <label 
                    htmlFor="login-password" 
                    style={{ 
                      fontSize: isMobile ? "13px" : "14px", 
                      fontWeight: "600", 
                      marginBottom: "8px", 
                      display: "block",
                      color: "#374151"
                    }}
                  >
                    Password
                  </label>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px 36px 10px 12px" : "12px 40px 12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#219297";
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      position: "absolute",
                      right: "8px",
                      top: isMobile ? "30px" : "34px",
                      padding: "6px",
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {showPassword ? <VisibilityOff fontSize={isMobile ? "small" : "medium"} /> : <Visibility fontSize={isMobile ? "small" : "medium"} />}
                  </IconButton>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loginLoading}
                  sx={{
                    py: isMobile ? 1.25 : 1.75,
                    backgroundColor: "#219297",
                    color: "#ffffff",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "15px",
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1a7a7d",
                      boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {loginLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
                      {isMobile ? "Logging in..." : "Logging in..."}
                    </Box>
                  ) : (
                    "Login"
                  )}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setShowPasswordField(false);
                    setLoginPassword("");
                    setSignupEmail("");
                  }}
                  sx={{
                    mt: 2,
                    color: "#219297",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: isMobile ? "12px" : "14px",
                    "&:hover": {
                      backgroundColor: "#f0fdfa",
                    },
                  }}
                >
                  ← Change Email Address
                </Button>
              </Box>
            </form>
          ) : !showOtpField ? (
            <>
              <form onSubmit={handleSignupSubmit}>
                <Box>
                  <Box mb={3}>
                    <label 
                      htmlFor="signup-email" 
                      style={{ 
                        fontSize: isMobile ? "13px" : "14px", 
                        fontWeight: "600", 
                        marginBottom: "8px", 
                        display: "block",
                        color: "#374151"
                      }}
                    >
                      Email Address
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      style={{
                        width: "100%",
                        padding: isMobile ? "12px 14px" : "14px 16px",
                        fontSize: isMobile ? "14px" : "15px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        boxSizing: "border-box",
                        outline: "none",
                        transition: "all 0.2s ease",
                        fontFamily: "inherit",
                        backgroundColor: "#fafafa",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#219297";
                        e.target.style.backgroundColor = "#ffffff";
                        e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.backgroundColor = "#fafafa";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={signupLoading}
                    sx={{
                      py: isMobile ? 1.25 : 1.75,
                      backgroundColor: "#219297",
                      color: "#ffffff",
                      borderRadius: "10px",
                      fontWeight: 600,
                      fontSize: isMobile ? "14px" : "15px",
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                      "&:hover": {
                        backgroundColor: "#1a7a7d",
                        boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                      "&:disabled": {
                        backgroundColor: "#cbd5e1",
                        boxShadow: "none",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {signupLoading ? (
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={20} sx={{ color: "teal", mr: 1.5 }} />
                        {isMobile ? "Verifying..." : "Verifying..."}
                      </Box>
                    ) : (
                      "Continue with Email"
                    )}
                  </Button>
                </Box>
              </form>

              <Box sx={{ my: isMobile ? 2 : 3, display: "flex", alignItems: "center" }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ px: 2, color: "text.secondary", fontSize: isMobile ? "12px" : "14px" }}>
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin
                  theme="filled_blue"
                  text="continue_with"
                  onSuccess={handleGoogleSignIn}
                  onError={() => console.log("Google login failed")}
                  size={isMobile ? "medium" : "large"}
                />
              </Box>

              {googleSignInLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={isMobile ? 20 : 24} />
                  <Typography variant="body2" sx={{ ml: 1, fontSize: isMobile ? "12px" : "14px" }}>
                    Signing in with Google...
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <Box>
                <Box 
                  sx={{ 
                    mb: 3,
                    p: isMobile ? 1.5 : 2,
                    backgroundColor: "#f0fdfa",
                    borderRadius: "10px",
                    border: "1px solid #ccfbf1",
                    textAlign: "center"
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "13px" : "14px" }}>
                    Code sent to
                  </Typography>
                  <Typography variant="body1" fontWeight="600" sx={{ color: "#219297", mt: 0.5, fontSize: isMobile ? "14px" : "16px" }}>
                    {signupEmail}
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <label 
                    htmlFor="otp-input" 
                    style={{ 
                      fontSize: isMobile ? "13px" : "14px", 
                      fontWeight: "600", 
                      marginBottom: "8px", 
                      display: "block",
                      color: "#374151"
                    }}
                  >
                    Verification Code
                  </label>
                  <input
                    id="otp-input"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    style={{
                      width: "100%",
                      padding: isMobile ? "12px 14px" : "14px 16px",
                      fontSize: isMobile ? "16px" : "18px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "all 0.2s ease",
                      fontFamily: "monospace",
                      letterSpacing: "0.3em",
                      textAlign: "center",
                      fontWeight: "600",
                      backgroundColor: "#fafafa",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#219297";
                      e.target.style.backgroundColor = "#ffffff";
                      e.target.style.boxShadow = "0 0 0 3px rgba(33, 146, 151, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={otpLoading}
                  sx={{
                    py: isMobile ? 1.25 : 1.75,
                    backgroundColor: "#219297",
                    color: "#ffffff",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "15px",
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1a7a7d",
                      boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {otpLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
                      {isMobile ? "Verifying..." : "Verifying code..."}
                    </Box>
                  ) : (
                    "Verify & Continue"
                  )}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setShowOtpField(false);
                    setOtp("");
                  }}
                  sx={{
                    mt: 2,
                    color: "#219297",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: isMobile ? "12px" : "14px",
                    "&:hover": {
                      backgroundColor: "#f0fdfa",
                    },
                  }}
                >
                  ← Change Email Address
                </Button>
              </Box>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={loginDialogOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: isMobile ? "16px" : "32px",
            maxHeight: isMobile ? "calc(100% - 32px)" : "calc(100% - 64px)",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 2 : 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            width: "100%",
            maxWidth: isMobile ? "calc(100vw - 32px)" : "500px",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          pt: isMobile ? 3 : 4, 
          px: isMobile ? 3 : 4,
          flexShrink: 0,
        }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              sx={{
                width: isMobile ? 48 : 64,
                height: isMobile ? 48 : 64,
                borderRadius: "50%",
                backgroundColor: "#e0f7f7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: isMobile ? 24 : 32, color: "#219297" }}>
                lock
              </Icon>
            </Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ mb: 1, color: "#1a1a1a" }}>
              Welcome Back!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              maxWidth: 380,
              fontSize: isMobile ? "13px" : "14px",
            }}>
              Sign in to access your dashboard
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          px: isMobile ? 3 : 4, 
          pb: isMobile ? 3 : 4, 
          pt: isMobile ? 1 : 3,
          overflow: "auto",
          flex: 1,
        }}>
          {renderLoginForm()}
        </DialogContent>
      </Dialog>
      
      <Modal
        open={addDomainModalOpen}
        onClose={() => setAddDomainModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: isMobile ? 1 : 2,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: isMobile ? "calc(100vw - 32px)" : 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: isMobile ? 2.5 : 4,
            maxHeight: isMobile ? "calc(100vh - 32px)" : "auto",
            overflow: "auto",
          }}
        >
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" gutterBottom>
            Add New Domain
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} sx={{ fontSize: isMobile ? "13px" : "14px" }}>
            Register a new domain with Ansera AI
          </Typography>

          <form onSubmit={handleAddDomainSubmit}>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12}>
                <Box mb={2}>
                  <label htmlFor="domainName" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Website Domain Name
                  </label>
                  <input
                    id="domainName"
                    name="domainName"
                    placeholder="example.com"
                    value={domainFormData.domainName}
                    onChange={handleDomainFormChange}
                    required
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px 12px" : "12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box mb={2}>
                  <label htmlFor="websitePlatform" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Website Platform
                  </label>
                  <SoftSelect
                    value={domainFormData.websitePlatform}
                    options={websitePlatforms}
                    onChange={(value) => handleDomainSelectChange("websitePlatform", value)}
                    placeholder="Select Website Platform"
                    required
                    sx={{
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: isMobile ? "10px 12px" : "12px 14px",
                        fontSize: isMobile ? "13px" : "14px",
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box mb={3}>
                  <label htmlFor="companyName" style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    placeholder="Ex: Sample Inc."
                    value={domainFormData.companyName}
                    onChange={handleDomainFormChange}
                    required
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px 12px" : "12px 14px",
                      fontSize: isMobile ? "13px" : "14px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={domainRegistrationLoading}
                  sx={{
                    py: isMobile ? 1.25 : 1.5,
                    backgroundColor: "#219297",
                    color: "#ffffff",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    fontSize: isMobile ? "14px" : "15px",
                    "&:hover": {
                      backgroundColor: "#1a7a7d",
                    },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                      cursor: "not-allowed",
                    }
                  }}
                >
                  {domainRegistrationLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                      {isMobile ? "Adding..." : "Adding Domain..."}
                    </Box>
                  ) : (
                    "Add Domain"
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
    </>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;